import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Trash2, Loader2 } from "lucide-react";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { createFaq } from "@/lib/api";
import FAQView from "@/components/previews/FAQView";
import PortalNotesFields from "@/components/PortalNotesFields";

export default function FAQBuilder() {
  const nav = useNavigate();
  const [title, setTitle] = useState("Frequently asked");
  const [description, setDescription] = useState("Everything you need to know.");
  const [items, setItems] = useState([
    { question: "What is PortalStudio?", answer: "A tiny builder for embeddable components." },
    { question: "Do I need an account?", answer: "Nope — just create and copy the URL." },
  ]);
  const [portals, setPortals] = useState([]);
  const [notes, setNotes] = useState("");
  const [tags, setTags] = useState([]);
  const [folderId, setFolderId] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const data = useMemo(() => ({ title, description, items, portals, notes, tags, folder_id: folderId }),
                      [title, description, items, portals, notes, tags, folderId]);

  const addItem = () => setItems((l) => [...l, { question: "", answer: "" }]);
  const removeItem = (i) => setItems((l) => l.filter((_, idx) => idx !== i));
  const updateItem = (i, field, value) =>
    setItems((l) => l.map((it, idx) => (idx === i ? { ...it, [field]: value } : it)));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) { toast.error("Title is required"); return; }
    const valid = items.filter((x) => x.question.trim() && x.answer.trim());
    if (valid.length === 0) { toast.error("Add at least one Q&A"); return; }

    setSubmitting(true);
    try {
      const res = await createFaq({ ...data, items: valid });
      toast.success("FAQ created");
      nav(`/success/faq/${res.id}`);
    } catch (err) {
      toast.error("Failed to create. Try again.");
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        <div className="mb-10">
          <div className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-400 mb-3">New Component</div>
          <h1 className="font-display text-3xl md:text-4xl tracking-tight font-semibold text-zinc-950">Build an FAQ</h1>
          <p className="text-zinc-600 mt-2">Collapsible accordion. Keep answers short and human.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <form data-testid="builder-form" onSubmit={onSubmit} className="md:col-span-7 space-y-8">
            <section className="border border-zinc-200 bg-white p-6 rounded-md space-y-5">
              <div className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-400">Heading</div>
              <div>
                <Label className="text-xs text-zinc-500">Title</Label>
                <Input
                  data-testid="faq-title-input"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label className="text-xs text-zinc-500">Description</Label>
                <Textarea
                  data-testid="faq-description-input"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="mt-1.5 resize-none"
                  rows={2}
                />
              </div>
            </section>

            <section className="border border-zinc-200 bg-white p-6 rounded-md space-y-4">
              <div className="flex items-center justify-between">
                <div className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-400">Questions</div>
                <span className="text-xs text-zinc-500">{items.length} item{items.length === 1 ? "" : "s"}</span>
              </div>

              <div className="space-y-4">
                {items.map((q, i) => (
                  <div key={i} className="border border-zinc-200 rounded-md p-4 bg-[#FAFAFA] space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-[11px] font-mono text-zinc-400">#{String(i + 1).padStart(2, "0")}</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        data-testid={`remove-item-btn-${i}`}
                        onClick={() => removeItem(i)}
                        className="text-zinc-400 hover:text-red-600 h-7 w-7"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                    <Input
                      data-testid={`faq-item-question-${i}`}
                      placeholder="Question"
                      value={q.question}
                      onChange={(e) => updateItem(i, "question", e.target.value)}
                      className="font-medium"
                    />
                    <Textarea
                      data-testid={`faq-item-answer-${i}`}
                      placeholder="Answer"
                      value={q.answer}
                      onChange={(e) => updateItem(i, "answer", e.target.value)}
                      rows={2}
                      className="resize-none"
                    />
                  </div>
                ))}
              </div>

              <Button
                type="button"
                variant="outline"
                data-testid="add-item-btn"
                onClick={addItem}
                className="w-full mt-2 rounded-md border-dashed border-zinc-300 hover:bg-zinc-50"
              >
                <Plus className="h-4 w-4 mr-2" /> Add question
              </Button>
            </section>

            <PortalNotesFields portals={portals} setPortals={setPortals}
                              notes={notes} setNotes={setNotes}
                              tags={tags} setTags={setTags}
                              folderId={folderId} setFolderId={setFolderId} />

            <Button
              type="submit"
              data-testid="generate-embed-btn"
              disabled={submitting}
              className="h-12 px-6 rounded-md bg-zinc-950 text-white hover:bg-zinc-800 transition-all duration-200"
            >
              {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Generate embed URL
            </Button>
          </form>

          <div className="md:col-span-5">
            <div className="md:sticky md:top-24">
              <div className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-400 mb-3">Live Preview</div>
              <div data-testid="preview-container" className="border border-zinc-200 rounded-md bg-white overflow-hidden">
                <div className="border-b border-zinc-100 px-4 py-2 flex items-center gap-1.5">
                  <span className="h-2.5 w-2.5 rounded-full bg-zinc-200" />
                  <span className="h-2.5 w-2.5 rounded-full bg-zinc-200" />
                  <span className="h-2.5 w-2.5 rounded-full bg-zinc-200" />
                  <span className="ml-3 text-[11px] font-mono text-zinc-400">embed/faq/preview</span>
                </div>
                <div className="p-6 bg-white min-h-[480px]">
                  <FAQView data={data} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
